import React, { useCallback, useEffect, useRef, useState } from "react";
import { MoreVertical, Paperclip, Send, CheckCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { memberApi, streamChat, imgUrl, errMsg } from "../lib/api";
import { useSite } from "../context/SiteContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";

const fmt = (iso) => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

function Bubble({ m, avatar, onImage }) {
  const mine = m.sender === "fan";
  return (
    <div className={`flex gap-3 ${mine ? "justify-end" : "justify-start"} mem-pop`} data-testid={`msg-${m.sender}`}>
      {!mine && <img src={avatar} alt="" className="w-[46px] h-[46px] rounded-full object-cover shrink-0 mt-1" />}
      <div className={`mem-bubble ${mine ? "mem-bubble-me" : "mem-bubble-maya"} ${m.image_url ? "!p-2 max-w-[400px] w-full" : ""}`}>
        {m.image_url && (
          <button onClick={() => onImage(imgUrl(m.image_url))} className="block w-full overflow-hidden rounded-xl" data-testid="msg-image">
            <img src={imgUrl(m.image_url)} alt="" className="w-full aspect-[4/3] object-cover object-[50%_25%] hover:scale-[1.03] transition-transform duration-500" />
          </button>
        )}
        <div className={m.image_url ? "px-2 pt-2 pb-1" : ""}>
          {m.text && <p className="whitespace-pre-wrap">{m.text}{m.streaming && <span className="mem-caret" />}</p>}
          <p className="mem-time">{m.created_at ? fmt(m.created_at) : ""}{mine && <CheckCheck className="inline w-3.5 h-3.5 ml-1 text-[#e8365f]" />}</p>
        </div>
      </div>
    </div>
  );
}

export default function ChatView({ user, onUnreadChange }) {
  const { img } = useSite();
  const avatar = img("avatar", "/img/g1.jpg");
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [typing, setTyping] = useState(false);
  const [viewer, setViewer] = useState(null);
  const endRef = useRef(null);
  const busyRef = useRef(false);
  const msgsRef = useRef([]);
  msgsRef.current = msgs;

  const merge = (incoming) => setMsgs((prev) => {
    const ids = new Set(prev.map((m) => m.id));
    const add = incoming.filter((m) => !ids.has(m.id));
    return add.length ? [...prev, ...add] : prev;
  });

  useEffect(() => {
    memberApi.get("/member/chat").then((r) => { setMsgs(r.data); onUnreadChange?.(); })
      .catch((e) => toast.error(errMsg(e, "Could not load chat"))).finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const poll = useCallback(async () => {
    if (busyRef.current) return;
    const list = msgsRef.current.filter((m) => m.created_at && !m.temp);
    const after = list.length ? list[list.length - 1].created_at : undefined;
    try {
      const { data } = await memberApi.get("/member/chat", { params: after ? { after } : {} });
      if (data.length) { merge(data); setTyping(false); }
    } catch (e) { /* silent */ }
  }, []);
  useEffect(() => { const t = setInterval(poll, 5000); return () => clearInterval(t); }, [poll]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [msgs, typing]);

  const send = async (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t || busy) return;
    setText(""); setBusy(true); busyRef.current = true; setTyping(true);
    const tempId = `tmp-${Date.now()}`;
    setMsgs((p) => [...p, { id: tempId, temp: true, sender: "fan", text: t, created_at: new Date().toISOString() }]);
    const streamId = `stream-${Date.now()}`;
    try {
      await streamChat(t, (ev) => {
        if (ev.type === "fan") setMsgs((p) => p.map((m) => (m.id === tempId ? ev.message : m)));
        if (ev.type === "delta") {
          setTyping(false);
          setMsgs((p) => {
            const i = p.findIndex((m) => m.id === streamId);
            if (i === -1) return [...p, { id: streamId, temp: true, streaming: true, sender: "maya", text: ev.content, created_at: new Date().toISOString() }];
            const c = [...p]; c[i] = { ...c[i], text: c[i].text + ev.content }; return c;
          });
        }
        if (ev.type === "done") {
          setTyping(false);
          if (ev.message) setMsgs((p) => [...p.filter((m) => m.id !== streamId), ev.message]);
          else if (ev.human) setTyping(true);
        }
      });
    } catch (err) {
      setTyping(false);
      setMsgs((p) => p.filter((m) => m.id !== tempId && m.id !== streamId));
      setText(t);
      toast.error(err.message || "Message failed");
    } finally {
      setBusy(false); busyRef.current = false;
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="hidden lg:flex items-center gap-4 px-6 h-[84px] border-b border-[#f1dfe0] bg-[#fdf5f3]/70">
        <div className="relative"><img src={avatar} alt="" className="w-[52px] h-[52px] rounded-full object-cover" /><span className="mem-online" /></div>
        <div><p className="mem-brand text-[22px] leading-none">Maya</p><p className="text-[14px] text-[#2f9e5a] mt-1">Online now</p></div>
        <p className="ml-auto mem-hand text-[30px] text-[#3a2d30]">Just you and me <span className="font-sans text-[20px]">♡</span></p>
        <DropdownMenu>
          <DropdownMenuTrigger className="p-2 ml-2 rounded-full hover:bg-[#f6e3e6]" data-testid="chat-menu"><MoreVertical className="w-5 h-5" /></DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => endRef.current?.scrollIntoView({ behavior: "smooth" })}>Jump to latest</DropdownMenuItem>
            <DropdownMenuItem onClick={poll}>Refresh</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex-1 overflow-y-auto mem-scroll px-4 lg:px-6 py-5 space-y-4" data-testid="mem-chat-messages">
        {loading && <div className="grid place-items-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#e8365f]" /></div>}
        {msgs.map((m) => <Bubble key={m.id} m={m} avatar={avatar} onImage={setViewer} />)}
        {typing && (
          <div className="flex gap-3 items-center" data-testid="maya-typing">
            <img src={avatar} alt="" className="w-[46px] h-[46px] rounded-full object-cover" />
            <div className="mem-bubble mem-bubble-maya flex gap-1.5 items-center !py-4"><i className="dot" /><i className="dot" /><i className="dot" /></div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <form onSubmit={send} className="flex items-center gap-3 px-4 lg:px-6 py-3 lg:py-4 border-t border-[#f1dfe0] bg-[#fdf5f3]/70">
        <button type="button" className="p-2 text-[#6b5d60] hover:text-[#e8365f]" onClick={() => toast("Photo sharing from fans is coming soon")} aria-label="Attach"><Paperclip className="w-5 h-5" /></button>
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message..." maxLength={2000}
          className="mem-input flex-1" data-testid="mem-chat-input" />
        <button type="submit" disabled={busy || !text.trim()} className="mem-send" data-testid="mem-chat-send" aria-label="Send">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5 -ml-0.5" fill="currentColor" />}
        </button>
      </form>

      <Dialog open={!!viewer} onOpenChange={(o) => !o && setViewer(null)}>
        <DialogContent className="max-w-[560px] p-0 overflow-hidden bg-black border-0">
          <DialogTitle className="sr-only">Photo</DialogTitle>
          {viewer && <img src={viewer} alt="" className="w-full max-h-[85vh] object-contain" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
